import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState } from "react";
import {
  listCampaigns,
  saveCampaign,
  deleteCampaign,
  getCampaignQueue,
  runCampaignNow,
  sendTestMessage,
  listSends,
  syncEngagement,
} from "@/lib/campaigns.functions";
import {
  CAMPAIGN_TYPE_LABELS,
  defaultCopyFor,
  MERGE_FIELDS,
  renderMerge,
  renderSms,
  plainTextPreviewHtml,
  sampleContext,
  ASSIGNED_USER_SIGNATURE,
  ASSIGNED_USER_NAME,
  previewSenderName,
  previewSignature,
  isAssignedUserToken,
  type CampaignType,
  type DripCampaign,
  type DripSend,
} from "@/lib/campaigns";
import { DAY_LABELS } from "@/lib/settings";
import { SEND_WINDOW, clampSendTime } from "@/lib/campaigns";

export const Route = createFileRoute("/_authenticated/admin/campaigns")({
  component: CampaignsPage,
  head: () => ({ meta: [{ title: "Campaigns | KCN Admin" }] }),
});

const inputCls =
  "w-full border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none";

function blankCampaign(): Partial<DripCampaign> {
  const copy = defaultCopyFor("visibility_audit");
  return {
    name: "",
    campaign_type: "visibility_audit",
    ghl_tag: "",
    daily_cap: 10,
    send_days: [3, 4, 5],
    send_time: SEND_WINDOW.startLabel,
    sms_delay_minutes: 15,
    email_subject: copy.subject,
    email_format: "html",
    email_html: copy.html,
    email_text: copy.text,
    sms_body: copy.sms,
    sender_name: "",
    sender_email: "",
    sender_phone: "",
    sms_signature: "",
    priority: 0,
    is_active: false,
  };
}

type CampaignStats = {
  sent: number;
  opened: number;
  clicked: number;
  replied: number;
  optedOut: number;
};

/** Percentage of delivered emails, shown under the raw count. */
function rate(part?: number, whole?: number): string {
  if (!whole || !part) return "-";
  return `${Math.round((part / whole) * 100)}%`;
}

function CampaignsPage() {
  const listFn = useServerFn(listCampaigns);
  const saveFn = useServerFn(saveCampaign);
  const deleteFn = useServerFn(deleteCampaign);
  const queueFn = useServerFn(getCampaignQueue);
  const runFn = useServerFn(runCampaignNow);
  const testFn = useServerFn(sendTestMessage);
  const sendsFn = useServerFn(listSends);
  const syncFn = useServerFn(syncEngagement);

  const [campaigns, setCampaigns] = useState<DripCampaign[]>([]);
  const [stats, setStats] = useState<Record<string, CampaignStats>>({});
  const [draft, setDraft] = useState<Partial<DripCampaign> | null>(null);
  const [queue, setQueue] = useState<any>(null);
  const [sends, setSends] = useState<DripSend[]>([]);
  const [status, setStatus] = useState<string | null>(null);
  const [testStatus, setTestStatus] = useState<string | null>(null);
  const [testContact, setTestContact] = useState("");
  const [busy, setBusy] = useState(false);
  const [engagedOnly, setEngagedOnly] = useState(false);
  const visibleSends = engagedOnly
    ? sends.filter((s) => s.clicked_at || s.replied_at || s.opted_out_at)
    : sends;

  async function refresh() {
    try {
      const res: any = await listFn({});
      setCampaigns(res.campaigns ?? []);
      setStats(res.stats ?? {});
      setSends((await sendsFn({ data: {} })) as DripSend[]);
    } catch (e: any) {
      setStatus(e?.message ?? "Could not load campaigns");
    }
  }

  /** Pull email and SMS engagement straight from Agentmoves. */
  async function syncNow() {
    setBusy(true);
    setStatus("Checking Agentmoves for engagement...");
    try {
      const res: any = await syncFn({ data: {} });
      const t = res.totalOpens ?? 0;
      const tc = res.totalClicks ?? 0;
      const tr = res.totalReplies ?? 0;
      const ts = res.totalOptOuts ?? 0;
      setStatus(
        `Checked ${res.checked} send${res.checked === 1 ? "" : "s"}. Totals: ${t} open${t === 1 ? "" : "s"}, ${tc} click${tc === 1 ? "" : "s"}, ${tr} repl${tr === 1 ? "y" : "ies"}, ${ts} stop${ts === 1 ? "" : "s"}.${res.failed ? ` ${res.failed} could not be checked. Try Sync again.` : ""}`,
      );
      await refresh();
    } catch (e: any) {
      setStatus(e?.message ?? "Could not sync engagement");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const draftContext = useMemo(() => {
    const base = sampleContext();
    const rawName = (draft?.sender_name ?? "").trim();
    const previewName = previewSenderName(rawName);
    const first = isAssignedUserToken(rawName) ? previewName : previewName.split(" ")[0];
    return {
      ...base,
      ...(first ? { sender_first_name: first } : {}),
      ...(draft?.sms_signature?.trim()
        ? { signature: previewSignature(draft.sms_signature.trim()) }
        : {}),
    };
  }, [draft?.sender_name, draft?.sms_signature]);

  const preview = useMemo(() => {
    if (!draft?.sms_body) return "";
    return renderSms(draft.sms_body, draftContext);
  }, [draft?.sms_body, draftContext]);

  const emailFormat: "html" | "text" = draft?.email_format === "text" ? "text" : "html";

  const emailPreview = useMemo(() => {
    if (emailFormat === "text") {
      if (!draft?.email_text) return "";
      return plainTextPreviewHtml(renderMerge(draft.email_text, draftContext));
    }
    if (!draft?.email_html) return "";
    return renderMerge(draft.email_html, draftContext);
  }, [emailFormat, draft?.email_html, draft?.email_text, draftContext]);

  function edit(c: DripCampaign) {
    setDraft({ ...c });
    setQueue(null);
    setStatus(null);
    setTestStatus(null);
  }

  function updateDraft(patch: Partial<DripCampaign>) {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  }

  function changeType(type: CampaignType) {
    const copy = defaultCopyFor(type);
    setDraft((prev) => {
      if (!prev) return prev;
      const old = defaultCopyFor((prev.campaign_type ?? "visibility_audit") as CampaignType);
      // Swap a field only when it is empty or still matches the previous type's
      // default copy, so admin edits are preserved.
      const swap = (value: string | null | undefined, oldDefault: string, next: string) =>
        !value?.trim() || value === oldDefault ? next : value;
      return {
        ...prev,
        campaign_type: type,
        email_subject: swap(prev.email_subject, old.subject, copy.subject),
        email_html: swap(prev.email_html, old.html, copy.html),
        email_text: swap(prev.email_text, old.text, copy.text),
        sms_body: swap(prev.sms_body, old.sms, copy.sms),
      };
    });
  }

  async function save() {
    if (!draft) return;
    setBusy(true);
    setStatus(null);
    try {
      await saveFn({
        data: {
          id: draft.id,
          name: draft.name ?? "",
          campaign_type: (draft.campaign_type ?? "visibility_audit") as CampaignType,
          ghl_tag: draft.ghl_tag ?? "",
          daily_cap: Number(draft.daily_cap ?? 10),
          send_days: draft.send_days ?? [],
          send_time: clampSendTime(draft.send_time ?? SEND_WINDOW.startLabel),
          sms_delay_minutes: Number(draft.sms_delay_minutes ?? 0),
          email_subject: draft.email_subject ?? "",
          email_format: (draft.email_format === "text" ? "text" : "html") as "html" | "text",
          email_html: draft.email_html ?? "",
          email_text: draft.email_text ?? "",
          sms_body: draft.sms_body ?? "",
          sender_name: draft.sender_name ?? "",
          sender_email: draft.sender_email ?? "",
          sender_phone: draft.sender_phone ?? "",
          sms_signature: draft.sms_signature ?? "",
          priority: Number(draft.priority ?? 0),
          is_active: Boolean(draft.is_active),
        },
      });
      setStatus("Saved.");
      setDraft(null);
      await refresh();
    } catch (e: any) {
      setStatus(e?.message ?? "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    if (!window.confirm("Delete this campaign and its send history?")) return;
    await deleteFn({ data: { id } });
    setDraft(null);
    await refresh();
  }

  async function loadQueue(id: string) {
    setQueue({ loading: true });
    try {
      setQueue(await queueFn({ data: { id } }));
    } catch (e: any) {
      setQueue({ contacts: [], error: e?.message ?? "Could not load queue" });
    }
  }

  async function runNow(id: string) {
    if (!window.confirm("Send now? This messages real contacts.")) return;
    setBusy(true);
    setStatus("Sending...");
    try {
      const res: any = await runFn({ data: { id } });
      const r = res.results?.[0];
      setStatus(
        r
          ? r.skipped
            ? `Skipped: ${r.skipped}`
            : `Enrolled ${r.enrolled}, emails ${r.emailsSent}, texts ${r.smsSent}, failures ${r.failures}.`
          : "Nothing to do.",
      );
      await refresh();
    } catch (e: any) {
      setStatus(e?.message ?? "Run failed");
    } finally {
      setBusy(false);
    }
  }

  async function testSend(id: string) {
    if (!testContact.trim()) {
      setTestStatus("Enter an email address or Agentmoves contact ID.");
      return;
    }
    setBusy(true);
    setTestStatus("Sending test SMS, then email...");
    try {
      const res: any = await testFn({ data: { id, contactId: testContact.trim() } });
      setTestStatus(`SMS: ${res.sms}. Email: ${res.email}.`);
    } catch (e: any) {
      setTestStatus(`Test failed: ${e?.message ?? "Unknown error"}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-8">
      <header className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl">Campaigns</h1>
            <p className="mt-1 text-sm text-foreground/65">
              Tagged contacts, capped daily sends, one email plus one text per contact.
            </p>
          </div>
          <div className="flex max-w-full flex-wrap items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => void syncNow()}
              disabled={busy}
              aria-busy={busy}
              className="min-w-24 border border-border px-5 py-2.5 text-xs uppercase tracking-[0.18em] text-foreground/75 hover:border-primary disabled:cursor-wait disabled:opacity-60"
            >
              {busy ? "Syncing..." : "Sync"}
            </button>
            <Link
              to="/admin/settings"
              className="border border-border px-5 py-2.5 text-xs uppercase tracking-[0.18em] text-foreground/75 hover:border-primary"
            >
              Settings
            </Link>
            <button
              onClick={() => {
                setDraft(blankCampaign());
                setQueue(null);
              }}
              className="bg-primary px-5 py-2.5 text-xs uppercase tracking-[0.18em] text-primary-foreground"
            >
              New campaign
            </button>
          </div>
        </div>
        {status && (
          <p className="text-xs leading-5 text-foreground/70" role="status" aria-live="polite">
            {status}
          </p>
        )}
      </header>

      

      {/* Mobile: one tall detail card per campaign */}
      <div className="space-y-4 lg:hidden">
        {campaigns.length === 0 && (
          <div className="border border-border bg-surface px-4 py-6 text-sm text-foreground/60">No campaigns yet.</div>
        )}
        {campaigns.map((c) => (
          <article key={c.id} className="border border-border bg-surface p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="font-display text-lg leading-tight">{c.name}</h3>
                <p className="text-xs text-foreground/55">{CAMPAIGN_TYPE_LABELS[c.campaign_type]}</p>
              </div>
              <span className="shrink-0 text-xs uppercase tracking-[0.14em]">
                {c.is_active ? (
                  <span className="text-primary">Active</span>
                ) : (
                  <span className="text-foreground/55">{c.paused_reason ? "Paused" : "Off"}</span>
                )}
              </span>
            </div>

            <dl className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-foreground/55">Tag</dt>
                <dd className="text-right break-all">{c.ghl_tag}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-foreground/55">From</dt>
                <dd className="text-right">{c.sender_name?.trim() || "Default sender"}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-foreground/55">Cap</dt>
                <dd className="text-right">{c.daily_cap}/day</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-foreground/55">Days</dt>
                <dd className="text-right">
                  {DAY_LABELS.filter((d) => c.send_days?.includes(d.value)).map((d) => d.label).join(", ")} {c.send_time}
                </dd>
              </div>
            </dl>

            <div className="mt-4 grid grid-cols-2 gap-px border border-border bg-border">
              {[
                { label: "Sent", value: `${stats[c.id]?.sent ?? 0}` },
                { label: "Opened", value: `${stats[c.id]?.opened ?? 0} · ${rate(stats[c.id]?.opened, stats[c.id]?.sent)}` },
                { label: "Replies", value: `${stats[c.id]?.replied ?? 0} · ${rate(stats[c.id]?.replied, stats[c.id]?.sent)}` },
                { label: "Stops", value: `${stats[c.id]?.optedOut ?? 0} · ${rate(stats[c.id]?.optedOut, stats[c.id]?.sent)}` },
              ].map((m) => (
                <div key={m.label} className="bg-surface px-3 py-3">
                  <div className="text-[0.65rem] uppercase tracking-[0.14em] text-foreground/55">{m.label}</div>
                  <div className="mt-1 whitespace-nowrap text-base">{m.value}</div>
                </div>
              ))}
            </div>

            <div className="mt-4 flex flex-wrap gap-3 text-xs uppercase tracking-[0.14em]">
              <button onClick={() => edit(c)} className="border border-border px-4 py-2 hover:border-primary">Edit</button>
              <button onClick={() => loadQueue(c.id)} className="border border-border px-4 py-2 hover:border-primary">Queue</button>
              <button onClick={() => runNow(c.id)} disabled={busy} className="border border-border px-4 py-2 hover:border-primary disabled:opacity-40">Send now</button>
            </div>
          </article>
        ))}
      </div>

      <section className="hidden border border-border bg-surface overflow-x-auto lg:block">
        <table className="w-full text-sm table-scroll min-w-[920px]">
          <thead className="border-b border-border text-left text-xs uppercase tracking-[0.14em] text-foreground/55">
            <tr>
              <th className="px-4 py-3">Campaign</th>
              <th className="px-4 py-3">Tag</th>
              <th className="px-4 py-3">Cap</th>
              <th className="px-4 py-3">Days</th>
              <th className="px-4 py-3">Sent</th>
              <th className="px-4 py-3">Opened</th>
              <th className="px-4 py-3">Replies</th>
              <th className="px-4 py-3">Stops</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {campaigns.length === 0 && (
              <tr>
                <td colSpan={10} className="px-4 py-6 text-foreground/60">
                  No campaigns yet.
                </td>
              </tr>
            )}
            {campaigns.map((c) => (
              <tr key={c.id}>
                <td data-label="Campaign" className="px-4 py-3">
                  <div className="font-medium">{c.name}</div>
                  <div className="text-xs text-foreground/55">{CAMPAIGN_TYPE_LABELS[c.campaign_type]}</div>
                </td>
                <td data-label="Tag" className="px-4 py-3 text-foreground/75">
                  <div>{c.ghl_tag}</div>
                  <div className="text-xs text-foreground/50">
                    From: {c.sender_name?.trim() || "Default sender"}
                  </div>
                </td>
                <td data-label="Cap" className="px-4 py-3">{c.daily_cap}/day</td>
                <td data-label="Days" className="px-4 py-3 text-foreground/75">
                  {DAY_LABELS.filter((d) => c.send_days?.includes(d.value)).map((d) => d.label).join(", ")}{" "}
                  {c.send_time}
                </td>
                <td data-label="Sent" className="px-4 py-3">{stats[c.id]?.sent ?? 0}</td>
                <td data-label="Opened" className="hidden lg:table-cell px-4 py-3 whitespace-nowrap">
                  <span>{stats[c.id]?.opened ?? 0}</span>{" "}
                  <span className="text-xs text-foreground/50">&middot; {rate(stats[c.id]?.opened, stats[c.id]?.sent)}</span>
                </td>
                <td data-label="Replies" className="px-4 py-3 whitespace-nowrap">
                  <span>{stats[c.id]?.replied ?? 0}</span>{" "}
                  <span className="text-xs text-foreground/50">&middot; {rate(stats[c.id]?.replied, stats[c.id]?.sent)}</span>
                </td>
                <td data-label="Stops" className="px-4 py-3 whitespace-nowrap">
                  <span>{stats[c.id]?.optedOut ?? 0}</span>{" "}
                  <span className="text-xs text-foreground/50">&middot; {rate(stats[c.id]?.optedOut, stats[c.id]?.sent)}</span>
                </td>
                <td data-label="Status" className="px-4 py-3">
                  {c.is_active ? (
                    <span className="text-primary">Active</span>
                  ) : (
                    <span className="text-foreground/55">{c.paused_reason ? "Paused" : "Off"}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex flex-wrap justify-end gap-3 text-xs uppercase tracking-[0.14em]">
                    <button onClick={() => edit(c)} className="hover:text-primary">
                      Edit
                    </button>
                    <button onClick={() => loadQueue(c.id)} className="hover:text-primary">
                      Queue
                    </button>
                    <button onClick={() => runNow(c.id)} disabled={busy} className="hover:text-primary disabled:opacity-40">
                      Send now
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {queue && (
        <section className="border border-border bg-surface p-4">
          <h2 className="font-display text-lg">Next queue</h2>
          {queue.loading && <p className="text-sm text-foreground/60">Loading...</p>}
          {queue.error && <p className="text-sm text-primary">{queue.error}</p>}
          {Array.isArray(queue.contacts) && queue.contacts.length > 0 && (
            <ul className="mt-2 divide-y divide-border text-sm">
              {queue.contacts.map((c: any) => (
                <li key={c.id} className="py-2">
                  <span className="font-medium">{c.name}</span>{" "}
                  <span className="text-foreground/60">
                    {c.email} {c.phone}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {Array.isArray(queue.contacts) && queue.contacts.length === 0 && !queue.error && (
            <p className="mt-2 text-sm text-foreground/60">Nobody eligible right now.</p>
          )}
        </section>
      )}

      {draft && (
        <section className="border border-border bg-surface">
          <div className="border-b border-border px-4 py-3">
            <h2 className="font-display text-lg">{draft.id ? "Edit campaign" : "New campaign"}</h2>
          </div>
          <div className="grid gap-4 px-4 py-4 md:grid-cols-2">
            <div>
              <label className="text-xs uppercase tracking-[0.14em] text-foreground/60">Name</label>
              <input className={inputCls} value={draft.name ?? ""} onChange={(e) => updateDraft({ name: e.target.value })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.14em] text-foreground/60">Type</label>
              <select
                className={inputCls}
                value={draft.campaign_type}
                onChange={(e) => changeType(e.target.value as CampaignType)}
              >
                <option value="visibility_audit">Visibility Audit</option>
                <option value="roundtable">Roundtable invite</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.14em] text-foreground/60">Agentmoves tag</label>
              <input className={inputCls} value={draft.ghl_tag ?? ""} onChange={(e) => updateDraft({ ghl_tag: e.target.value })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.14em] text-foreground/60">Daily cap</label>
              <input
                type="number"
                min={0}
                max={50}
                className={inputCls}
                value={draft.daily_cap ?? 10}
                onChange={(e) => updateDraft({ daily_cap: Number(e.target.value) })}
              />
            </div>
            <div>
              <span className="text-xs uppercase tracking-[0.14em] text-foreground/60">Send days</span>
              <div className="mt-2 flex flex-wrap gap-2">
                {DAY_LABELS.map((d) => {
                  const on = draft.send_days?.includes(d.value);
                  return (
                    <button
                      key={d.value}
                      type="button"
                      onClick={() =>
                        updateDraft({
                          send_days: on
                            ? (draft.send_days ?? []).filter((v) => v !== d.value)
                            : [...(draft.send_days ?? []), d.value].sort(),
                        })
                      }
                      className={`border px-3 py-1.5 text-xs uppercase tracking-[0.14em] ${
                        on ? "border-primary text-primary" : "border-border text-foreground/60"
                      }`}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="text-xs uppercase tracking-[0.14em] text-foreground/60">Send time</label>
                <input
                  type="time"
                  min={SEND_WINDOW.startLabel}
                  max={SEND_WINDOW.endLabel}
                  className={inputCls}
                  value={draft.send_time ?? SEND_WINDOW.startLabel}
                  onChange={(e) => updateDraft({ send_time: e.target.value })}
                  onBlur={(e) => updateDraft({ send_time: clampSendTime(e.target.value) })}
                />
                <p className="mt-1 text-xs text-foreground/50">Sends only go out {SEND_WINDOW.label}.</p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.14em] text-foreground/60">SMS delay (min)</label>
                <input
                  type="number"
                  min={0}
                  className={inputCls}
                  value={draft.sms_delay_minutes ?? 0}
                  onChange={(e) => updateDraft({ sms_delay_minutes: Number(e.target.value) })}
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.14em] text-foreground/60">Email subject</label>
              <input
                className={inputCls}
                value={draft.email_subject ?? ""}
                onChange={(e) => updateDraft({ email_subject: e.target.value })}
              />
            </div>
            <div className="md:col-span-2 border-t border-border pt-4">
              <span className="text-xs uppercase tracking-[0.14em] text-foreground/60">Sender for this campaign</span>
              <p className="mt-1 text-xs text-foreground/50">
                Leave a field blank to use the default from Settings.
              </p>
              <div className="mt-3 grid gap-4 md:grid-cols-3">
                <div>
                  <label className="text-xs uppercase tracking-[0.14em] text-foreground/60">Sender name</label>
                  <input
                    className={inputCls}
                    placeholder="Default from Settings"
                    value={draft.sender_name ?? ""}
                    onChange={(e) => updateDraft({ sender_name: e.target.value })}
                  />
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => updateDraft({ sender_name: ASSIGNED_USER_NAME })}
                      className="border border-border px-3 py-1 text-xs uppercase tracking-[0.14em] hover:border-primary"
                    >
                      Use assigned user
                    </button>
                    <span className="text-xs text-foreground/55">
                      Sends as whoever the contact is assigned to in Agentmoves.
                    </span>
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.14em] text-foreground/60">Sender email</label>
                  <input
                    className={inputCls}
                    placeholder="Default from Settings"
                    value={draft.sender_email ?? ""}
                    onChange={(e) => updateDraft({ sender_email: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.14em] text-foreground/60">Sender phone</label>
                  <input
                    className={inputCls}
                    placeholder="Default from Settings"
                    value={draft.sender_phone ?? ""}
                    onChange={(e) => updateDraft({ sender_phone: e.target.value })}
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="text-xs uppercase tracking-[0.14em] text-foreground/60">Signature</label>
                <input
                  className={inputCls}
                  placeholder="Default from Settings"
                  value={draft.sms_signature ?? ""}
                  onChange={(e) => updateDraft({ sms_signature: e.target.value })}
                />
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => updateDraft({ sms_signature: ASSIGNED_USER_SIGNATURE })}
                    className="border border-border px-3 py-1 text-xs uppercase tracking-[0.14em] hover:border-primary"
                  >
                    Use assigned user
                  </button>
                  <span className="text-xs text-foreground/55">
                    Inserts {"{{user.email_signature}}"}, so Agentmoves swaps in the contact owner's
                    own signature at send time.
                  </span>
                </div>
              </div>

            </div>
            <div className="md:col-span-2">
              <span className="text-xs uppercase tracking-[0.14em] text-foreground/60">Email format</span>
              <div className="mt-2 flex flex-wrap gap-2">
                {([
                  ["html", "Branded HTML"],
                  ["text", "Plain text"],
                ] as const).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => updateDraft({ email_format: value })}
                    className={`border px-3 py-1.5 text-xs uppercase tracking-[0.14em] ${
                      emailFormat === value
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border hover:border-primary"
                    }`}
                  >
                    {label}
                  </button>
                ))}
                <span className="self-center text-xs text-foreground/55">
                  Branded HTML carries the KCN logo and CTA button. Plain text sends like a personal note.
                </span>
              </div>
            </div>
            {emailFormat === "html" ? (
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-[0.14em] text-foreground/60">
                  Email body (branded KCN)
                </label>
                <textarea
                  rows={10}
                  className={`${inputCls} font-mono text-xs`}
                  value={draft.email_html ?? ""}
                  onChange={(e) => updateDraft({ email_html: e.target.value })}
                />
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      updateDraft({
                        email_html: defaultCopyFor((draft.campaign_type ?? "visibility_audit") as CampaignType).html,
                      })
                    }
                    className="border border-border px-3 py-1.5 text-xs uppercase tracking-[0.14em] hover:border-primary"
                  >
                    Reset to KCN template
                  </button>
                  <span className="text-xs text-foreground/55">
                    This branded HTML is what goes out. Edit the copy, keep the styling.
                  </span>
                </div>
              </div>
            ) : (
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-[0.14em] text-foreground/60">Email body (plain text)</label>
                <textarea
                  rows={12}
                  className={`${inputCls} text-sm`}
                  value={draft.email_text ?? ""}
                  onChange={(e) => updateDraft({ email_text: e.target.value })}
                />
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      updateDraft({
                        email_text: defaultCopyFor((draft.campaign_type ?? "visibility_audit") as CampaignType).text,
                      })
                    }
                    className="border border-border px-3 py-1.5 text-xs uppercase tracking-[0.14em] hover:border-primary"
                  >
                    Reset to KCN copy
                  </button>
                  <span className="text-xs text-foreground/55">
                    No logo, no styling. Merge fields still work.
                  </span>
                </div>
              </div>
            )}
            <div className="md:col-span-2">
              <span className="text-xs uppercase tracking-[0.14em] text-foreground/60">Email preview</span>
              <iframe
                title="Email preview"
                srcDoc={emailPreview || "<p style=\"font-family:sans-serif\">Nothing yet.</p>"}
                className={`mt-2 w-full border border-border bg-white ${
                  emailFormat === "text" ? "h-[360px]" : "h-[520px]"
                }`}
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-[0.14em] text-foreground/60">SMS body</label>
              <textarea
                rows={8}
                className={inputCls}
                value={draft.sms_body ?? ""}
                onChange={(e) => updateDraft({ sms_body: e.target.value })}
              />
              <p className="mt-1 text-xs text-foreground/55">Merge fields: {MERGE_FIELDS.join(" ")}</p>
            </div>
            <div className="md:col-span-2">
              <span className="text-xs uppercase tracking-[0.14em] text-foreground/60">Preview</span>
              <pre className="mt-2 whitespace-pre-wrap border border-border bg-background px-3 py-2 text-sm text-foreground/80">
                {preview || "Nothing yet."}
              </pre>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="text-xs uppercase tracking-[0.14em] text-foreground/60">Priority</label>
                <input
                  type="number"
                  min={0}
                  className={inputCls}
                  value={draft.priority ?? 0}
                  onChange={(e) => updateDraft({ priority: Number(e.target.value) })}
                />
              </div>
              <label className="flex items-end gap-2 pb-2 text-sm">
                <input
                  type="checkbox"
                  checked={Boolean(draft.is_active)}
                  onChange={(e) => updateDraft({ is_active: e.target.checked })}
                />
                Active
              </label>
            </div>
            {draft.id && (
              <div>
                <label className="text-xs uppercase tracking-[0.14em] text-foreground/60">Test email or contact ID</label>
                <div className="flex gap-2">
                  <input
                    className={inputCls}
                    placeholder="you@example.com"
                    value={testContact}
                    onChange={(e) => setTestContact(e.target.value)}
                  />
                  <button
                    onClick={() => draft.id && testSend(draft.id)}
                    disabled={busy}
                    className="whitespace-nowrap border border-border px-4 text-xs uppercase tracking-[0.14em] hover:border-primary disabled:opacity-40"
                  >
                    {busy ? "Sending..." : "Test send"}
                  </button>
                </div>
                {testStatus && (
                  <p
                    role="status"
                    aria-live="polite"
                    className="mt-2 border-l-2 border-primary bg-background px-3 py-2 text-sm text-foreground"
                  >
                    {testStatus}
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3 border-t border-border px-4 py-3">
            <button
              onClick={save}
              disabled={busy}
              className="bg-primary px-5 py-2.5 text-xs uppercase tracking-[0.18em] text-primary-foreground disabled:opacity-50"
            >
              {busy ? "Saving..." : "Save campaign"}
            </button>
            <button onClick={() => setDraft(null)} className="text-xs uppercase tracking-[0.18em] text-foreground/60 hover:text-foreground">
              Cancel
            </button>
            {draft.id && (
              <button
                onClick={() => remove(draft.id!)}
                className="ml-auto text-xs uppercase tracking-[0.18em] text-primary hover:opacity-80"
              >
                Delete
              </button>
            )}
          </div>
        </section>
      )}

      <section className="border border-border bg-surface">
        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b border-border px-4 py-3">
          <h2 className="font-display text-lg">Send history</h2>
          <div className="flex shrink-0 items-center gap-4">
            <label className="flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-foreground/60">
              <input
                type="checkbox"
                checked={engagedOnly}
                onChange={(e) => setEngagedOnly(e.target.checked)}
              />
              Engaged only
            </label>
          </div>
        </div>

        {/* Mobile: two columns, Contact + Status badges, no horizontal scroll */}
        <div className="md:hidden">
          {visibleSends.length === 0 && (
            <div className="px-4 py-6 text-sm text-foreground/60">
              {engagedOnly ? "No opens, replies or stops yet." : "Nothing sent yet."}
            </div>
          )}
          <ul className="divide-y divide-border">
            {visibleSends.slice(0, 100).map((s) => (
              <li
                key={s.id}
                className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="truncate font-medium">
                    {s.contact_name || s.ghl_contact_id}
                  </div>
                  <div className="truncate text-xs text-foreground/55">
                    {s.contact_email}
                  </div>
                  <div className="mt-0.5 text-xs text-foreground/45">
                    {new Date(s.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1 text-sm">
                  {s.opened_at && (
                    <span className="font-medium text-foreground/80">Opened</span>
                  )}
                  {s.clicked_at && (
                    <span className="font-medium text-foreground/80">Clicked</span>
                  )}
                  {s.replied_at && (
                    <span className="font-medium text-primary">Reply</span>
                  )}
                  {s.opted_out_at && (
                    <span className="font-semibold uppercase text-primary">STOP</span>
                  )}
                  {!s.opened_at && !s.clicked_at && !s.replied_at && !s.opted_out_at && (
                    <span className="text-foreground/40">-</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Desktop: full table */}
        <div className="hidden overflow-x-auto md:block">
        <table className="table-scroll w-full text-sm">
          <thead className="border-b border-border text-left text-xs uppercase tracking-[0.14em] text-foreground/55">
            <tr>
              <th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">Opened</th>
              <th className="px-4 py-3">Clicked</th>
              <th className="px-4 py-3">Reply</th>
              <th className="px-4 py-3">Stop</th>
              <th className="px-4 py-3">When</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {visibleSends.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-foreground/60">
                  {engagedOnly ? "No opens, replies or stops yet." : "Nothing sent yet."}
                </td>
              </tr>
            )}
            {visibleSends.slice(0, 100).map((s) => (
              <tr key={s.id}>
                <td data-label="Contact" className="px-4 py-3">
                  <div>{s.contact_name || s.ghl_contact_id}</div>
                  <div className="text-xs text-foreground/55">{s.contact_email}</div>
                </td>
                <td data-label="Opened" className="px-4 py-3 text-foreground/70">
                  {s.opened_at ? (
                    <div>
                      <span className="text-foreground/90">Yes</span>
                      <div className="text-xs text-foreground/50">
                        {new Date(s.opened_at).toLocaleString()}
                      </div>
                    </div>
                  ) : (
                    <span className="text-foreground/40">-</span>
                  )}
                </td>
                <td data-label="Clicked" className="px-4 py-3 text-foreground/70">
                  {s.clicked_at ? (
                    <div>
                      <span className="text-foreground/90">Yes</span>
                      <div className="text-xs text-foreground/50">
                        {new Date(s.clicked_at).toLocaleString()}
                      </div>
                    </div>
                  ) : (
                    <span className="text-foreground/40">-</span>
                  )}
                </td>
                <td data-label="Reply" className="px-4 py-3 text-foreground/70">
                  {s.replied_at ? (
                    <div>
                      <span className="font-medium text-primary">Reply</span>
                      <div className="text-xs text-foreground/50">
                        {new Date(s.replied_at).toLocaleString()}
                      </div>
                    </div>
                  ) : (
                    <span className="text-foreground/40">-</span>
                  )}
                </td>
                <td data-label="Stop" className="px-4 py-3 text-foreground/70">
                  {s.opted_out_at ? (
                    <div>
                      <span className="font-semibold uppercase text-primary">STOP</span>
                      <div className="text-xs text-foreground/50">
                        {new Date(s.opted_out_at).toLocaleString()}
                      </div>
                    </div>
                  ) : (
                    <span className="text-foreground/40">-</span>
                  )}
                </td>
                <td data-label="When" className="px-4 py-3 text-foreground/60">
                  {new Date(s.created_at).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </section>
    </div>
  );
}
